import {
  styled as styledMui,
  Box,
  Button,
  IconButton,
  BoxProps,
} from "@mui/material";
import { isClassroom } from "./utils";
import { ActivityV1Type } from "@pages/Contents/V2/Corporate/Catalog/AddActivity/types";

export const CardRow = styledMui(Box, {
  shouldForwardProp: (prop) => prop !== "selected" && prop !== "backgroundVariant",
})<{ selected?: boolean; backgroundVariant?: "card" | "default" }>(({ theme, selected, backgroundVariant = "card" }) => ({
  display: "flex",
  // alignItems: "center",
  gap: "16px",
  padding: "16px 20px",
  borderRadius: "12px",
  backgroundColor: backgroundVariant === "default" ? theme.palette.background.default : theme.palette.background.card || "#2A2A2A",
  border: "1px solid transparent",
  transition: "border-color 0.15s ease",
  ...(selected && {
    borderColor: theme.palette.borderAccent,
  }),
  "&:hover": {
    borderColor: selected ? theme.palette.borderAccent : theme.palette.divider,
  },
  [theme.breakpoints.down("lg")]: {
    flexDirection: "column",
    alignItems: "stretch",
    gap: "12px",
  },
}));

export const CheckboxArea = styledMui(Box)({
  display: "flex",
  alignItems: "center",
  flexShrink: 0,
});

export const IconArea = styledMui(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  color: theme.palette.text.primary,
}));

export const InfoArea = styledMui(Box)({
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  gap: "8px",
  flex: 1,
  minWidth: 0,
});

export const MetricsWrapper = styledMui(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "4px",
});

export const TitleRow = styledMui(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

export const SubtitleRow = styledMui(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: "4px",
  fontSize: "14px",
  lineHeight: "1.25rem",
  color: theme.palette.text.primary,
}));

export const SubtitleItem = styledMui(Box)({
  display: "flex",
  alignItems: "center",
  gap: "4px",
  minWidth: 0,
  "& span": {
    overflowWrap: "anywhere",
  },
  "& svg": {
    width: "14px",
    height: "14px",
    flexShrink: 0,
  },
});

export const MetricsRow = styledMui(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "6px",
  flexWrap: "wrap",
  [theme.breakpoints.down("lg")]: {
    gap: "16px",
  },
}));

export const DetailsContainer = styledMui(Box, {
  shouldForwardProp: (prop) => prop !== "detailed",
})<BoxProps & { activityType?: ActivityV1Type; detailed?: boolean }>(
  ({ theme, activityType, detailed }) => ({
    display: "flex",
    flexDirection: "column",
    gap: detailed ? 0 : "0.125rem",
  }),
);
export const DetailsWrapper = styledMui(Box)<
  BoxProps & { activityType?: ActivityV1Type }
>(({ theme, activityType }) => ({
  display: "flex",
  flexDirection: activityType && isClassroom(activityType) ? "row" : "column",
  lineHeight: "1rem",
  gap: "0.5rem",
}));
export const Bottom = styledMui(Box, {
  shouldForwardProp: (prop) => prop !== "detailed",
})<BoxProps & { activityType?: ActivityV1Type; detailed?: boolean }>(
  ({ theme, activityType, detailed }) => ({
    marginTop: detailed ? "4px" : 0,
  }),
);
export const Top = styledMui(Box)<BoxProps & { activityType?: ActivityV1Type }>(
  ({ theme, activityType }) => ({
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    flex: 1,
    gap: "16px",
  }),
);

export const HeadingBlock = styledMui(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "4px",
});

export const DetailsRow = styledMui(Box, {
  shouldForwardProp: (prop) => prop !== "detailed",
})<BoxProps & { detailed?: boolean }>(({ theme, detailed }) => ({
  display: "flex",
  alignItems: "center",
  fontSize: detailed ? "14px" : "0.75rem",
  lineHeight: detailed ? "1.15rem" : "1rem",
  color: detailed ? theme.palette.text.primary : theme.palette.text.description,
  [theme.breakpoints.down("lg")]: {
    whiteSpace: "nowrap",
  },
}));

export const MetricItem = styledMui(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "4px",
  fontSize: "12px",
  lineHeight: "14px",
  color: theme.palette.text.primary,
  "& svg": {
    width: "14px",
    height: "14px",
    color: theme.palette.text.primary,
  },
  [theme.breakpoints.down("lg")]: {
    whiteSpace: "nowrap",
  },
}));

export const MetricClickable = styledMui(Box)(({ theme }) => ({
  cursor: "pointer",
  textDecoration: "underline",
  textDecorationColor: theme.palette.text.tertiary,
  textUnderlineOffset: "2px",
  "&:hover": {
    color: theme.palette.text.primary,
  },
}));

export const MetricSeparator = styledMui(Box)(({ theme }) => ({
  width: "1px",
  height: "12px",
  backgroundColor: theme.palette.divider,
  margin: "0 4px",
  [theme.breakpoints.down("lg")]: {
    display: "none",
  },
}));

export const RightArea = styledMui(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  justifyContent: "space-between",
  flexShrink: 0,
  alignSelf: "stretch",
  gap: "12px",
  maxWidth: "60%",
  [theme.breakpoints.down("lg")]: {
    width: "100%",
    alignItems: "stretch",
    maxWidth: "100%",
  },
}));

export const BadgesRow = styledMui(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  flexWrap: "wrap",
  justifyContent: "flex-end",
  marginBottom: "1rem",
  [theme.breakpoints.down("lg")]: {
    justifyContent: "flex-start",
  },
}));

export const ActionsRow = styledMui(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  flexWrap: "wrap",
  [theme.breakpoints.down("lg")]: {
    justifyContent: "flex-start",
  },
}));

export const ActionBtn = styledMui(Button)(({ theme }) => ({
  minWidth: "auto",
  padding: "0.25rem 1rem",
  height: "2rem",
  fontSize: "0.75rem",
  fontWeight: 600,
  borderRadius: "8px",
  textTransform: "none",
  lineHeight: 1.5,
  borderColor: theme.palette.text.primary,
  backgroundColor: theme.palette.backgroundPrimary,
  color: theme.palette.text.primary,
  "&:hover": {
    borderColor: theme.palette.text.primary,
    backgroundColor: theme.palette.action.hover,
  },
}));

export const MoreBtn = styledMui(IconButton)(({ theme }) => ({
  width: "36px",
  height: "36px",
  border: `2px solid ${theme.palette.text.primary}`,
  borderRadius: "8px",
  color: theme.palette.text.primary,
  "&:hover": {
    borderColor: theme.palette.text.primary,
    backgroundColor: theme.palette.action.hover,
  },
}));

export const IconActionBtn = styledMui(IconButton)(({ theme }) => ({
  width: "36px",
  height: "36px",
  border: `2px solid ${theme.palette.text.primary}`,
  borderRadius: "8px",
  color: theme.palette.text.primary,
  "&:hover": {
    borderColor: theme.palette.text.primary,
    backgroundColor: theme.palette.action.hover,
  },
}));

export const IconActionGhostBtn = styledMui(IconButton)(({ theme }) => ({
  width: "36px",
  height: "36px",
  border: "none",
  borderRadius: "8px",
  backgroundColor: "transparent",
  color: theme.palette.primary.main,
  "&:hover": {
    backgroundColor: "transparent",
    opacity: 0.8,
  },
}));

export const PublishBtn = styledMui(Button)(({ theme }) => ({
  minWidth: "auto",
  padding: "6px 20px",
  fontSize: "13px",
  fontWeight: 600,
  borderRadius: "8px",
  textTransform: "none",
  lineHeight: 1.5,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  boxShadow: "none",
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
    boxShadow: "none",
  },
}));

export const MandatorySelectWrapper = styledMui(Box)(() => ({
  display: "flex",
  gap: "8px",
  marginTop: "8px",
  alignItems: "center",
}));
